/**
 * Aplica CSS variables de co-branding no documentElement.
 * Nunca altera --primary do tema Cork: isso quebra o dark mode até o reload.
 *
 * Tokens canônicos (--brand-*):
 * - primary/accent (+ rgb) → CTAs, links, estados ativos
 * - primary-hover / primary-soft → hover e fundos suaves
 * - header-bg / sidebar-bg → chrome (sidebar mais clara que o header)
 * - on-header → texto/ícones legíveis sobre o header
 *
 * Aliases de produto (--shell-*, --bs-primary, --dv2-*) herdam via SCSS
 * com fallback; aqui só garantimos --brand-* no :root.
 */

/**
 * @param {string|null|undefined} hex
 * @returns {string|null}
 */
export function normalizeBrandHex(hex) {
    if (typeof hex !== 'string') return null;
    const v = hex.trim();
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return null;
    if (v.length === 4) {
        return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
    }
    return v.toLowerCase();
}

/**
 * @param {string} hex
 * @returns {string|null} "r, g, b"
 */
export function hexToRgbTriplet(hex) {
    const n = normalizeBrandHex(hex);
    if (!n) return null;
    const full = n.slice(1);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((x) => Number.isNaN(x))) return null;
    return `${r}, ${g}, ${b}`;
}

/**
 * @param {string} hex
 * @returns {[number, number, number]|null}
 */
function hexToRgbChannels(hex) {
    const triplet = hexToRgbTriplet(hex);
    if (!triplet) return null;
    return triplet.split(',').map((x) => parseInt(x.trim(), 10));
}

/**
 * Luminância relativa WCAG (0–1).
 * @param {string} hex
 * @returns {number|null}
 */
export function relativeLuminance(hex) {
    const channels = hexToRgbChannels(hex);
    if (!channels) return null;
    const [r, g, b] = channels.map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Texto legível sobre um fundo de marca.
 * @param {string} bgHex
 * @returns {{ on: string, muted: string }}
 */
export function contrastOnBrand(bgHex) {
    const L = relativeLuminance(bgHex);
    if (L == null || L > 0.55) {
        return { on: '#0f172a', muted: 'rgba(15, 23, 42, 0.72)' };
    }
    return { on: '#ffffff', muted: 'rgba(255, 255, 255, 0.78)' };
}

/** Propriedades --brand-* gerenciadas por este módulo. */
export const BRAND_CSS_VAR_KEYS = [
    '--brand-primary',
    '--brand-accent',
    '--brand-primary-rgb',
    '--brand-accent-rgb',
    '--brand-primary-hover',
    '--brand-primary-soft',
    '--brand-accent-soft',
    '--brand-header-bg',
    '--brand-sidebar-bg',
    '--brand-sidebar-bg-dark',
    '--brand-body-bg',
    '--brand-content-bg',
    '--brand-content-bg-dark',
    '--brand-surface-bg',
    '--brand-surface-item-bg',
    '--brand-surface-bg-dark',
    '--brand-surface-item-bg-dark',
    '--brand-heading-bg',
    '--brand-heading-bg-end',
    '--brand-heading-bg-dark',
    '--brand-heading-bg-end-dark',
    '--brand-on-header',
    '--brand-on-header-muted',
    '--brand-on-primary',
    '--bs-primary',
    '--bs-primary-rgb',
    '--dv2-primary',
    '--dv2-primary-soft',
    '--dv2-primary-soft-dark',
];

/**
 * @param {{
 *   primary_color?: string|null,
 *   accent_color?: string|null,
 *   mode?: string|null,
 *   is_customized?: boolean,
 * }|null|undefined} branding
 * @param {HTMLElement} [root]
 */
export function applyBrandingCssVars(branding, root = document.documentElement) {
    if (!root || !branding || branding.is_customized === false) {
        clearBrandingCssVars(root);
        return;
    }

    const primary = normalizeBrandHex(branding.primary_color);
    const accent = normalizeBrandHex(branding.accent_color) || primary;
    if (!primary) {
        clearBrandingCssVars(root);
        return;
    }

    const primaryRgb = hexToRgbTriplet(primary);
    const accentRgb = hexToRgbTriplet(accent);
    const headerContrast = contrastOnBrand(primary);
    const onPrimary = contrastOnBrand(primary).on;

    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-accent', accent);
    if (primaryRgb) root.style.setProperty('--brand-primary-rgb', primaryRgb);
    if (accentRgb) root.style.setProperty('--brand-accent-rgb', accentRgb);

    // Derivados via color-mix (Chrome/Firefox/Safari modernos)
    root.style.setProperty('--brand-primary-hover', `color-mix(in srgb, ${primary} 82%, #000000)`);
    root.style.setProperty('--brand-primary-soft', `color-mix(in srgb, ${primary} 14%, #ffffff)`);
    root.style.setProperty('--brand-accent-soft', `color-mix(in srgb, ${accent} 14%, #ffffff)`);

    // Hierarquia de superfícies (claro → um pouco mais escuro)
    // body < content < widget < correction-item
    root.style.setProperty('--brand-header-bg', primary);
    root.style.setProperty('--brand-sidebar-bg', `color-mix(in srgb, ${primary} 16%, #ffffff)`);
    root.style.setProperty('--brand-sidebar-bg-dark', `color-mix(in srgb, ${primary} 28%, #141c2c)`);
    root.style.setProperty('--brand-body-bg', `color-mix(in srgb, ${primary} 4%, #ffffff)`);
    root.style.setProperty('--brand-content-bg', `color-mix(in srgb, ${primary} 8%, #ffffff)`);
    root.style.setProperty('--brand-surface-bg', `color-mix(in srgb, ${primary} 14%, #ffffff)`);
    root.style.setProperty('--brand-surface-item-bg', `color-mix(in srgb, ${primary} 18%, #ffffff)`);
    // Superfícies dark: mais escuras (navy profundo + leve tinta da marca)
    root.style.setProperty('--brand-content-bg-dark', `color-mix(in srgb, ${primary} 10%, #080e1a)`);
    root.style.setProperty('--brand-surface-bg-dark', `color-mix(in srgb, ${primary} 12%, #0a1220)`);
    root.style.setProperty('--brand-surface-item-bg-dark', `color-mix(in srgb, ${primary} 16%, #0e1726)`);
    // Heading do wallet: bem mais escuro (gradiente da primary)
    root.style.setProperty('--brand-heading-bg', `color-mix(in srgb, ${primary} 55%, #000000)`);
    root.style.setProperty('--brand-heading-bg-end', primary);
    root.style.setProperty('--brand-heading-bg-dark', `color-mix(in srgb, ${primary} 28%, #060818)`);
    root.style.setProperty('--brand-heading-bg-end-dark', `color-mix(in srgb, ${primary} 42%, #0a1220)`);
    root.style.setProperty('--brand-on-header', headerContrast.on);
    root.style.setProperty('--brand-on-header-muted', headerContrast.muted);
    root.style.setProperty('--brand-on-primary', onPrimary);

    // Bootstrap + tokens V2 fora do scoped do shell
    root.style.setProperty('--bs-primary', primary);
    if (primaryRgb) root.style.setProperty('--bs-primary-rgb', primaryRgb);
    root.style.setProperty('--dv2-primary', primary);
    root.style.setProperty('--dv2-primary-soft', `color-mix(in srgb, ${primary} 14%, #ffffff)`);
    root.style.setProperty('--dv2-primary-soft-dark', `color-mix(in srgb, ${primary} 22%, transparent)`);

    root.dataset.brandMode = branding.mode === 'white_label' ? 'white_label' : 'co_brand';
    root.classList.add('has-co-branding');
}

/**
 * @param {HTMLElement} [root]
 */
export function clearBrandingCssVars(root = document.documentElement) {
    if (!root) return;
    BRAND_CSS_VAR_KEYS.forEach((k) => root.style.removeProperty(k));
    // Se builds antigas deixaram --primary inline, remove sem depender do tema Cork.
    if (root.style.getPropertyValue('--primary')) {
        root.style.removeProperty('--primary');
    }
    delete root.dataset.brandMode;
    root.classList.remove('has-co-branding');
}

/**
 * @returns {{ applyBrandingCssVars: typeof applyBrandingCssVars, clearBrandingCssVars: typeof clearBrandingCssVars }}
 */
export function useApplyBrandingCssVars() {
    return {
        applyBrandingCssVars,
        clearBrandingCssVars,
        normalizeBrandHex,
        hexToRgbTriplet,
        relativeLuminance,
        contrastOnBrand,
    };
}
